import { Trans } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { Alert } from 'reactstrap'
import styled from 'styled-components/macro'

import { useRoot } from '../../app/useRoot'
import { mediaDesktop } from '../../common/Responsive'
import { useMapPanning } from '../../components/Map/useMapPanning'
import { useLocationResolver } from '../../hooks/useLocationResolver'
import { i18n } from '../../i18n'
import { AddressSearchInput } from './destination/AddressSearchInput'
import GeolocationButton from './destination/GeolocationButton'
import { ResolvedAddress } from './destination/ResolvedAddress'
import { useAddressSearch } from './hooks/useAddressSearch'

const StyledDeliveryAddressSearch = styled.div`
  width: 90%;
  min-width: 14rem;
  margin: 1rem auto;
  text-align: center;

  div.container {
    display: flex;
    border-radius: 1rem;
    border: 1px solid #d4d4d4;
    background-color: white;
    ${mediaDesktop`
    height: 2.3rem;
    `}
  }
  input {
    height: auto;
    background: transparent;
    border: none;
    &:focus {
      box-shadow: initial;
    }
    &.hidden {
      display: none;
    }
  }

  div.gps {
    color: ${({ theme }) => theme.secondary};
    cursor: pointer;
    position: relative;

    display: grid;
    align-items: center;
  }

  div.resolved__address {
    margin: 0.4rem 0.7rem 0.4rem 0.7rem;
    font-size: 1rem;
    color: green;
    width: 100%;
    cursor: pointer;
    &.hidden {
      display: none;
    }
    &.wait {
      display: flex;
      justify-content: center;
      font-size: 0.5rem;
      cursor: wait;
    }
  }

  .alert {
    pointer-events: none;
    margin-bottom: 0;
  }
`

export const DeliveryAddressSearch = observer(() => {
  const { location } = useRoot()

  const { results, searching, onSearch, phrase } = useAddressSearch()
  const resolver = useLocationResolver()

  React.useEffect(() => {
    if (searching) {
      location.log('set resolving due to active search')
      location.setIsResolving(searching)
    }
  }, [location, searching])

  const panTo = useMapPanning()

  React.useEffect(() => {
    if (results === null) {
      return
    }

    if (results.length === 0) {
      location.log('no result from direct geocoder api')
      location.setAsNotResolved()
      return
    }

    const [singleResult] = results
    const { gps, address } = singleResult

    if (!gps) return

    resolver(gps, phrase)

    location.log(`panning to result gps ${gps.latitude} / ${gps.longitude}`)
    panTo(gps)

    location.log(`found address ${address}`)

    if (
      location.latitude !== gps.latitude &&
      location.longitude !== gps.longitude
    ) {
      location.resetResolution()
    }

    location.setSearchResult(singleResult)
  }, [location, panTo, phrase, resolver, results])

  return (
    <StyledDeliveryAddressSearch>
      <div className="container">
        <ResolvedAddress
          address={location.address || ''}
          isVisible={location.hasAddress}
          isResolving={location.isResolving}
          onValidClick={location.clearAddress}
        />
        <AddressSearchInput
          inputProps={{
            placeholder: i18n.t`Type address or choose it from map`,
            autoComplete: 'shipping street-address',
          }}
          onSearch={onSearch}
          isVisible={!(location.hasAddress || location.isResolving)}
        />
        <GeolocationButton />
      </div>
      <ErrorMessage />
    </StyledDeliveryAddressSearch>
  )
})

function ErrorMessage() {
  const { location } = useRoot()
  if (!location.hasResolutionResult) {
    return null
  }
  if (location.isResolved === false) {
    return (
      <Alert color="danger">
        <Trans>Address was not recognized.</Trans>
      </Alert>
    )
  }
  if (location.isStreetAddress === false) {
    return (
      <Alert color="warning">
        <Trans>Please enter address with house number.</Trans>
      </Alert>
    )
  }
  if (location.inDeliveryLocation === false) {
    return (
      <Alert color="warning">
        <Trans>
          Meal can't be delivered to your chosen address by any restaurant.
        </Trans>
      </Alert>
    )
  }
  return null
}
